import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash.throttle';
import { utils } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import ElementSettingsContext from '../helpers/element-settings-context';
import getBatchValues from '../helpers/get-batch-values';

const { SliderPattern } = designSystem;

const getPopoverProps = (prop) => {
  if (!prop) return {};

  return {
    ...prop,
    content: prop.renderContent?.() ?? prop.content ?? '',
  };
};

const isIndeterminate = (values) =>
  values.some((value, index, allValues) => value !== allValues[0]);

const getMaxValue = (sliderValues) =>
  [...sliderValues].sort((a, b) => parseFloat(b) - parseFloat(a))[0];

const toNumber = (value, fallback) => {
  const numericValue = parseFloat(value);

  return Number.isFinite(numericValue) ? numericValue : fallback;
};

function SliderControl({
  id,
  elementIds,
  resolvedState = null,
  targets,
  title,
  iconGlyph,
  mixedLabel,
  step,
  min,
  max,
  items = [],
  popover = null,
  valuePopover = null,
  computeFallbackValue = null,
  supportedUnits = [],
  onChange,
  onAfterChange = null,
}) {
  const { resolvedState: defaultResolvedState } = useContext(ElementSettingsContext);
  const hasUnits = supportedUnits.length > 0;

  const resolveValue = useCallback(
    (sliderValues, isMixed) => {
      const value = isMixed ? getMaxValue(sliderValues) : sliderValues[0];

      return hasUnits ? value : toNumber(value, min);
    },
    [hasUnits, min]
  );

  const values = useMemo(
    () =>
      getBatchValues(resolvedState ?? defaultResolvedState, {
        elementIds,
        targets,
        computeFallbackValue,
      }),
    [resolvedState, defaultResolvedState, elementIds, targets, computeFallbackValue]
  );
  const flattenedValues = values.flat();
  const indeterminate = isIndeterminate(flattenedValues);
  const currentValue = resolveValue(flattenedValues, indeterminate);
  const isMultipleElements = elementIds.length > 1;

  const popoverProps = useMemo(() => getPopoverProps(popover), [popover]);
  const valuePopoverProps = useMemo(() => getPopoverProps(valuePopover), [valuePopover]);

  const emitChange = useCallback(
    ({ targetId, targets: itemTargets, value }) => {
      onChange({
        controlId: targetId,
        elementIds,
        targets: itemTargets,
        value,
        skipTracking: true,
      });
    },
    [elementIds, onChange]
  );

  const emitAfterChange = useCallback(
    ({ targetId, targets: itemTargets, value }) => {
      const payload = {
        controlId: targetId,
        elementIds,
        targets: itemTargets,
        value,
      };

      (onAfterChange ?? onChange)(payload);
    },
    [elementIds, onChange, onAfterChange]
  );

  const throttledHandleChange = useMemo(
    () =>
      throttle((targetId, itemTargets, value) => {
        emitChange({
          targetId,
          targets: itemTargets,
          value: `${value}`,
        });
      }, 50),
    [emitChange]
  );

  const createSliderChangeHandler = useCallback(
    (targetId, itemTargets) => (value) => {
      if (!utils.isNumeric(parseFloat(value))) {
        return;
      }

      throttledHandleChange(targetId, itemTargets, value);
    },
    [throttledHandleChange]
  );

  const createSliderAfterChangeHandler = useCallback(
    (targetId, itemTargets) => (value) => {
      if (!utils.isNumeric(parseFloat(value))) {
        return;
      }

      throttledHandleChange.cancel();

      emitAfterChange({
        targetId,
        targets: itemTargets,
        value: `${value}`,
      });
    },
    [emitAfterChange, throttledHandleChange]
  );

  const sliderItems = useMemo(
    () =>
      items.length > 0
        ? items.map((item, index) => {
            const subValues = [values[index]].flat();
            const subIndeterminate = isMultipleElements && isIndeterminate(subValues);
            const subCurrentValue = resolveValue(subValues, subIndeterminate);

            return {
              value: subCurrentValue,
              iconGlyph: item.iconGlyph,
              popover: getPopoverProps(item.popover),
              indeterminate: subIndeterminate,
              ...(item.valuePopover && {
                valuePopoverProps: getPopoverProps(item.valuePopover),
              }),
              onChange: createSliderChangeHandler(item.id, item.targets),
              onAfterChange: createSliderAfterChangeHandler(item.id, item.targets),
            };
          })
        : null,
    [
      values,
      createSliderAfterChangeHandler,
      createSliderChangeHandler,
      isMultipleElements,
      resolveValue,
      items,
    ]
  );

  return (
    <SliderPattern
      value={currentValue}
      title={title}
      items={sliderItems}
      iconGlyph={iconGlyph}
      mixedLabel={mixedLabel}
      step={step}
      min={min}
      max={max}
      supportedUnits={supportedUnits}
      indeterminate={indeterminate}
      popoverProps={popoverProps}
      {...(valuePopover && { valuePopoverProps })}
      onChange={createSliderChangeHandler(id, targets)}
      onAfterChange={createSliderAfterChangeHandler(id, targets)}
    />
  );
}

SliderControl.propTypes = {
  id: PropTypes.string.isRequired,
  elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  resolvedState: PropTypes.object,
  targets: PropTypes.arrayOf(PropTypes.string).isRequired,
  title: PropTypes.string.isRequired,
  iconGlyph: PropTypes.string.isRequired,
  mixedLabel: PropTypes.string.isRequired,
  step: PropTypes.oneOfType([PropTypes.number, PropTypes.func]).isRequired,
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.func]).isRequired,
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.func]).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      iconGlyph: PropTypes.string,
      popover: PropTypes.object,
      valuePopover: PropTypes.object,
      targets: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  popover: PropTypes.object,
  valuePopover: PropTypes.object,
  computeFallbackValue: PropTypes.func,
  supportedUnits: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  onAfterChange: PropTypes.func,
};

export default SliderControl;
